<template>
  <div class="email">
    <p class="text-h6">メールアドレス変更</p>
    <v-row justify="center">
      <v-col
        cols="12"
        sm="10"
        md="8"
        lg="6"
      >
        <validation-observer ref="observer">
          <div v-if="displayMode.input">
            <v-form @submit.prevent="changeEmail">
              <v-card tail outlined>
                <v-card-text>
                  <div class="my-3">
                    メールアドレスを変更します。
                  </div>
                  <v-alert
                    border="left"
                    colored-border
                    type="info"
                    elevation="2"
                    style="font-size: 90%"
                  >
                    新メールアドレス宛に認証コードを送信します。受信制限されている方は、<b class="red--text">事前に必ず</b>「@kenchare.net」ドメインを受信できるように設定をお願い致します。<br/>
                    メールアドレス変更後、ログイン画面に遷移します。
                  </v-alert>
                  <v-alert
                    v-if="alert"
                    border="left"
                    colored-border
                    type="error"
                    elevation="2"
                    style="font-size: 90%"
                  >
                    {{ error }}
                  </v-alert>
                  <validation-provider
                    name="新メールアドレス"
                    rules="required|email"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="inputEmail"
                      label="新メールアドレス"
                      :error-messages="errors"
                      prepend-icon="mdi-email-outline"
                      hide-details="auto"
                      class="ma-6 mt-4"
                      outlined
                      dense
                    >
                    </v-text-field>
                  </validation-provider>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn type="submit" color="primary" :loading="isLoading">変更</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </div>

          <div v-if="displayMode.confirm">
            <v-form @submit.prevent="verifySubmit">
              <v-card tail outlined>
                <v-card-text>
                  <v-alert
                    border="left"
                    colored-border
                    type="info"
                    elevation="2"
                    style="font-size: 90%"
                  >
                    ご登録いただいた新メールアドレスへ認証コードを送信しました。認証コードを入力してください。<br/>
                    認証コード入力後、ログイン画面に遷移しますので再度ログインしてください。<br/>
                    <p style="text-indent: -1em;" class="mt-3 pl-4">
                      <b>※認証コードが届かない場合</b><br/>
                      メールアドレスが間違っているか、受信制限されている可能性があります。<br/>
                      メールアドレスを間違えた場合は「メールアドレス変更を取り消す」をクリックし、再度メールアドレス変更をしてください。<br/>
                      受信制限されている方は、「@kenchare.net」ドメインを受信できるように設定したうえで「認証コード再発行」をクリックしてください。<br/>
                      それでも届かない場合、ログイン画面下部のサポートデスクまでお問い合わせください。<br/><br/>
                      <b>メールサーバにより、認証コード受信までに時差が発生することがあります。</b><br/>
                      なお、コードを再送信した場合は、最後に受信した認証コードを入力いただく必要がありますので、ご注意ください。
                    </p>
                  </v-alert>
                  <v-alert
                    v-if="alert"
                    border="left"
                    colored-border
                    type="error"
                    elevation="2"
                    style="font-size: 90%"
                  >
                    {{ error }}
                  </v-alert>
                  <validation-provider
                    name="認証コード"
                    rules="required|confirmCode"
                    v-slot="{ errors }"
                    ref="observer"
                  >
                    <v-text-field
                      label="認証コード"
                      v-model="inputVerify"
                      :error-messages="errors"
                      hide-details="auto"
                      class="ma-6"
                      outlined
                      dense />
                  </validation-provider>
                </v-card-text>
                <v-card-actions style="flex-wrap: wrap;">
                  <v-btn text @click="codeRetry" color="primary">認証コード再発行</v-btn>
                  <v-btn text @click="rollback" color="primary">メールアドレス変更を取り消す</v-btn>
                  <v-spacer></v-spacer>
                  <v-btn type="submit" color="primary" class="ma-3" :loading="isLoading">送信</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </div>
        </validation-observer>
      </v-col>
    </v-row>
    <Dialog ref="confirm"/>
    <SuccessSnackbar ref="snackbar"/>
    <v-dialog
      v-model="dialog"
      persistent
      :max-width="options.width"
    >
      <v-card dense flat>
        <v-card-title class="headline">
        </v-card-title>
        <v-card-text>
          {{message}}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click.native="clickOK()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import GraphqlAPI from '../../utils/GraphqlAPI'
import { Auth, I18n } from 'aws-amplify'
import { translate } from '@/i18n'
import { required, email } from 'vee-validate/dist/rules'
import { localize, extend, ValidationObserver, ValidationProvider, setInteractionMode } from 'vee-validate'
import ja from 'vee-validate/dist/locale/ja.json'
import Theme from '../../utils/Theme'
const Dialog = () => import('../util/Dialog.vue')
const SuccessSnackbar = () => import('../util/SuccessSnackbar.vue')

// バリデーションの定義
setInteractionMode('eager')
localize('ja', ja)
extend('required', required)
extend('email', email)
extend('confirmCode', (value) => {
  const inputCode = value.replace(/[０-９]/g, function (s) {
    return String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
  })
  return /^[0-9０－９]{6}$/.test(inputCode) || '{_field_} は6桁の数字で入力してください'
})

function getUser () {
  return Auth.currentAuthenticatedUser().then((user) => {
    if (!user) {
      return null
    }
    return user
  }).catch(e => new Error(e))
}

const updateUserEmailMutation = (email) => `
  mutation updateUser {
    updateUser(
      input: {
        email: "${email}"
      }
    ) {
      id
      email
    }
  }
`
const updateUserEmail = async (email) => {
  const { data: { updateUser: data } } = await GraphqlAPI(updateUserEmailMutation(email))
  return {
    id: data.id,
    email: data.email
  }
}

const rollbackEmailMutation = () => `
  mutation rollbackUserpoolEmail {
    rollbackUserpoolEmail
    {
      email
      email_verified
    }
  }
`

const rollbackEmail = async () => {
  const { data: { rollbackUserpoolEmail: data } } = await GraphqlAPI(rollbackEmailMutation())
  return {
    email: data.email,
    email_verified: data.email_verified
  }
}

export default {
  name: 'email',
  mixins: [Theme],
  data () {
    return {
      user: {},
      displayMode: {},
      inputEmail: '',
      inputVerify: '',
      isLoading: false,
      error: '',
      alert: false,
      dialog: false,
      resolve: null,
      reject: null,
      message: null,
      options: {
        width: 400
      }
    }
  },
  computed: {
    getEmailVerified () {
      return this.$store.getters.getEmailVerified
    }
  },
  components: {
    ValidationProvider,
    ValidationObserver,
    SuccessSnackbar,
    Dialog
  },
  mounted () {
    this.displayMode = this.changeDisplayMode('input')
    getUser()
      .then((val) => {
        this.user = val
        this.$store.dispatch('setEmailVerified', val.attributes.email_verified)
      })
      .catch(e => this.setError(e))

    if (this.getEmailVerified) {
      this.displayMode = this.changeDisplayMode('input')
    } else {
      this.displayMode = this.changeDisplayMode('confirm')
    }
  },
  methods: {
    changeDisplayMode (state) {
      return {
        input: state === 'input',
        confirm: state === 'confirm'
      }
    },
    async changeEmail () {
      this.isLoading = true
      this.alert = false
      const isValidateError = await this.$refs.observer.validate()
      if (!isValidateError) {
        this.isLoading = false
        this.alert = true
        this.error = '入力内容にエラーがあります。確認してください。'
        return false
      }
      const user = this.user
      if (this.inputEmail === this.user.attributes.email) {
        this.isLoading = false
        this.alert = true
        this.error = '現在登録しているメールアドレスです。'
      } else {
        await Auth.updateUserAttributes(user, { email: this.inputEmail })
          .then(async (data) => {
            this.displayMode = this.changeDisplayMode('confirm')
            this.$store.dispatch('setEmailVerified', false)
            this.$emit('fetchChangeEmail')
            this.isLoading = false
          })
          .catch((e) => {
            this.setError(e)
          })
        this.$refs.observer.reset()
      }
    },
    async rollback () {
      this.isLoading = true
      if (await this.$refs.confirm.open('', 'メールアドレスの変更を取り消します。よろしいですか。')) {
        try {
          const user = this.user
          const rollbackData = await rollbackEmail()
          Auth.updateUserAttributes(user, { email: rollbackData.email, email_verified: rollbackData.email_verified })
          this.user.attributes.email = rollbackData.email
          this.$store.dispatch('setEmailVerified', rollbackData.email_verified)
          this.$emit('fetchChangeEmail')
          if (this.$store.getters.getRegisteredCourse) {
            this.$router.push('result')
          } else {
            this.$router.push('course')
          }
        } catch (e) {
          this.setError(e)
        }
      }
      this.isLoading = false
    },
    async verifySubmit () {
      this.$refs.observer.reset()
      this.isLoading = true
      this.alert = false
      const isValidateError = await this.$refs.observer.validate()
      if (!isValidateError) {
        this.isLoading = false
        this.alert = true
        this.error = '入力内容にエラーがあります。確認してください。'
        return false
      }
      Auth.verifyCurrentUserAttributeSubmit('email', this.inputVerify)
        .then(async (data) => {
          try {
            if (this.inputEmail) {
              await updateUserEmail(this.inputEmail)
            } else {
              await updateUserEmail(this.user.attributes.email)
            }
          } catch (e) {
            this.setError(e)
          }
          if (await this.open('認証に成功しました。再度ログインを行ってください。')) {
            this.isLoading = false
            this.$vuetify.theme.themes.light = this.$_getTheme(false)
            this.signOut()
          }
        })
        .catch((e) => {
          this.setError(e)
        })
    },
    async codeRetry () {
      try {
        await Auth.verifyCurrentUserAttribute('email')
        await this.$refs.snackbar.open('認証コードを再送信しました。')
        this.isLoading = false
      } catch (e) {
        this.setError(e)
      }
    },
    async signOut () {
      try {
        Auth.signOut()
          .then(() => {
            this.$store.dispatch('signOut')
            this.$router.push('/login')
          })
          .catch((e) => {
            this.setError(e)
          })
      } catch (e) {
        this.setError(e)
      }
    },
    open (message) {
      this.dialog = true
      this.message = message
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    clickOK () {
      this.resolve(true)
      this.dialog = false
    },
    setError (e) {
      this.isLoading = false
      const errorMessage = I18n.get(e.message || e)
      if (errorMessage === 'CustomMessage failed with error email is already registered in other account.') {
        this.error = `${translate('auth.errors.userEmailiAlreadyExist')}`
      } else if (errorMessage === 'Invalid verification code provided, please try again.') {
        this.error = `${translate('auth.errors.invalidVerificationCode')}`
      } else if (errorMessage === 'Attempt limit exceeded, please try after some time.') {
        this.error = `${translate('auth.errors.limitExceededException')}`
      } else {
        this.error = `${translate('auth.errors.unexpectedError')}`
      }
      this.alert = true
    }
  }
}
</script>
